import { useContext, useRef, useState } from 'react';

import { Button, Header } from '@GxT-Team/gxt-components';

import { AuthoringHeaderProps } from './types';

import { GeneralizationContext } from '../../../../contexts/GeneralizationContext';
import { useMappingSessionAPI } from '../../../../services/api/mappingSession/useMappingSessionAPI';
import { useNavigate } from 'react-router-dom';
import { notifyError } from '../../../../helpers/utils';
import { StatusType } from '../../../../services/api/types';

const AuthoringHeader = ({
  isAutoMapping = false,
  isLoading,
  handleSave,
  handleClose,
}: AuthoringHeaderProps) => {
  const { sessionId, acceptedSuggestions, setSessionStatus } = useContext(GeneralizationContext);
  const [showSaved, setShowSaved] = useState(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { updateMappingSessionStatus } = useMappingSessionAPI();
  const { mutateAsync: updateSessionStatus, isPending } = updateMappingSessionStatus;

  const navigate = useNavigate();

  const statusUpdateHandler = async (status: string) => {
    if (sessionId) {
      try {
        await updateSessionStatus({ status, id: sessionId });
        setSessionStatus(status);
        // eslint-disable-next-line no-empty
      } catch (err) {
        console.error(err);
      }
    } else {
      console.error('Unable to find session ID');
    }
  };

  const handleFinish = async () => {
    if (acceptedSuggestions?.length) {
      await statusUpdateHandler('finished');
      navigate('/catalog');
    } else {
      notifyError('No mappings found');
    }
  };

  return (
    <Header>
      <Header.Heading className="!text-primary-400">
        {isAutoMapping ? 'Mapping' : 'Authoring'}
      </Header.Heading>
      <Header.Actions>
        {!isAutoMapping && (
          <>
            <Button
              variant="secondary"
              onClick={async () => {
                if (isLoading) return;
                if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
                setShowSaved(false);
                await handleSave();
                setShowSaved(true);
                savedTimerRef.current = setTimeout(() => setShowSaved(false), 1500);
              }}
              className="flex items-center gap-1.5 min-w-[72px] justify-center">
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Saving...
                </>
              ) : showSaved ? (
                'Saved ✓'
              ) : (
                'Save'
              )}
            </Button>
          </>
        )}
        <Button variant="negative" onClick={handleClose} disabled={isPending}>
          Close
        </Button>
        <div className="h-10 border width-2 border-neutral-200" />
        {!isAutoMapping ? (
          <>
            <Button
              variant="primary"
              onClick={() => statusUpdateHandler(StatusType.READY_TO_REVIEW)}
              disabled={isPending}>
              Ready to Review
            </Button>
            <Button
              variant="secondary"
              onClick={() => statusUpdateHandler(StatusType.COMPLETED)}
              disabled={isPending}>
              Document Complete
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleFinish}>
            Finish
          </Button>
        )}
      </Header.Actions>
    </Header>
  );
};

export default AuthoringHeader;
